import React, { useEffect, useState, useRef, useMemo } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { clearAuthSession, getStoredUser } from '../utils/authStorage';
import { clearProfile, getStoredProfile } from '../utils/profileStorage';
import { getSetting, preloadSettings } from '../services/settingsService';
import './SiteDashboard.css'; // Create a new CSS file for grid styling

const formatRole = (role) => {
  if (!role) return '';
  const roleLower = role.toLowerCase();
  if (roleLower === 'user') return 'Viewer';
  if (roleLower === 'technician') return 'Developer';
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
};

const SiteDashboard = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(() => getStoredProfile());
  const [refreshInterval, setRefreshInterval] = useState(30); // Default to 30 seconds
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    ownerEmail: '',
    platform: 'wordpress',
    wpAppUsername: '',
    wpAppPassword: '',
  });
  const [showModal, setShowModal] = useState(false); // State to manage modal visibility
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const syncProfile = () => {
      setProfile(getStoredProfile());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('profile-updated', syncProfile);
      window.addEventListener('storage', syncProfile);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('profile-updated', syncProfile);
        window.removeEventListener('storage', syncProfile);
      }
    };
  }, []);

  const fetchSites = () => {
    setLoading(true);
    setError(null);
    axios.get('/api/sites')
      .then(res => {
        // Add placeholder data for status, uptime, seo score, and last scan
        setSites(res.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching sites:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load websites. Please try again.');
        setLoading(false);
        setSites([]);
      });
  };

  // Load refresh interval setting on mount
  useEffect(() => {
    const loadRefreshInterval = async () => {
      try {
        // Preload settings to populate cache
        await preloadSettings();
        const interval = await getSetting('dashboardRefreshInterval', 30);
        setRefreshInterval(interval || 30);
      } catch (error) {
        console.error('Failed to load refresh interval setting:', error);
        setRefreshInterval(30); // Fallback to default
      }
    };
    loadRefreshInterval();
  }, []);

  // Listen for settings changes (when user saves new settings)
  useEffect(() => {
    const handleSettingsChange = async () => {
      try {
        const interval = await getSetting('dashboardRefreshInterval', 30);
        setRefreshInterval(interval || 30);
      } catch (error) {
        console.error('Failed to reload refresh interval setting:', error);
      }
    };

    // Listen for storage events (when settings are saved in another tab/window)
    window.addEventListener('storage', handleSettingsChange);
    
    // Also listen for custom event if settings are saved in same window
    window.addEventListener('settings-updated', handleSettingsChange);

    return () => {
      window.removeEventListener('storage', handleSettingsChange);
      window.removeEventListener('settings-updated', handleSettingsChange);
    };
  }, []);

  // Set up auto-refresh with dynamic interval
  useEffect(() => {
    fetchSites();

    // Fetch data at the configured interval (convert seconds to milliseconds)
    const intervalMs = refreshInterval * 1000;
    const intervalId = setInterval(fetchSites, intervalMs);

    // Cleanup function to clear the interval when the component unmounts or interval changes
    return () => clearInterval(intervalId);
  }, [refreshInterval]); // Re-run when refreshInterval changes

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProfileAction = (action) => {
    setShowProfileMenu(false);
    if (action === 'my-profile') {
      // Navigate to User Management Profile page
      navigate('/users#profile');
    } else if (action === 'sign-out') {
      clearAuthSession();
      clearProfile();
      navigate('/login', { replace: true });
    }
  };

  const handleAddSite = () => {
    if (!formData.name || !formData.url) return;
    axios.post('/api/sites', formData)
      .then(res => {
        // Add placeholder data to the newly added site as well
        // Removed: const newSiteWithPlaceholderData = { ... };
        // Add a default status until the first backend check updates it
        const newlyAddedSite = { ...res.data, status: 'Checking...' };
        console.log('Adding new site to state:', newlyAddedSite);
        setSites([newlyAddedSite, ...sites]);
        setFormData({
          name: '',
          url: '',
          ownerEmail: '',
          platform: 'wordpress',
          wpAppUsername: '',
          wpAppPassword: '',
        });
        setShowModal(false); // Close modal after adding site
        setError(null); // Clear any previous errors
      })
      .catch(err => {
        console.error('Error adding site:', err);
        setError(err.response?.data?.message || err.message || 'Failed to add website. Please try again.');
      });
  };

  const handleRemoveSite = (siteId, siteName) => {
    const confirmed = window.confirm(`Are you sure you want to remove "${siteName}"? This action cannot be undone.`);
    if (!confirmed) return;

    axios.delete(`/api/sites/${siteId}`)
      .then(() => {
        // Remove the site from the state
        setSites(sites.filter(site => site._id !== siteId));
        setError(null);
      })
      .catch(err => {
        console.error('Error removing site:', err);
        setError(err.response?.data?.message || err.message || 'Failed to remove website. Please try again.');
      });
  };

  const userDisplayName = useMemo(() => {
    if (profile?.name?.trim()) return profile.name.trim();
    if (profile?.email?.trim()) return profile.email.trim();
    return 'WebMonitor User';
  }, [profile?.name, profile?.email]);

  const userEmail = profile?.email?.trim() || '';
  const authUser = getStoredUser();
  const userRole = authUser?.role ? formatRole(authUser.role) : '';
  const userRoleLower = authUser?.role?.toLowerCase() || '';
  const privilegedRoles = ['admin', 'manager', 'technician'];
  const hasFullAccess = privilegedRoles.includes(userRoleLower);

  const userInitials = useMemo(() => {
    const source = profile?.name || profile?.email || 'WM';
    return source
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || 'WM';
  }, [profile?.name, profile?.email]);

  const userAvatar = useMemo(() => {
    // Check authUser.profilePicture first, then profile.avatar
    return authUser?.profilePicture || profile?.avatar || null;
  }, [authUser?.profilePicture, profile?.avatar]);

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="main-content">
        <div className="header">
          <h1>Dashboard</h1>
          <div className="user-info" ref={profileMenuRef}>
            <button
              type="button"
              className="icon-button"
              aria-label="Notifications"
            >
              🔔
            </button>
            <button
              type="button"
              className="user-profile-button"
              onClick={() => setShowProfileMenu((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={showProfileMenu}
            >
              <span className="user-avatar">
                {userAvatar ? (
                  <img 
                    src={userAvatar} 
                    alt={userDisplayName}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  userInitials
                )}
              </span>
              <span className="user-meta">
                <span className="user-name">{userDisplayName}</span>
                {userRole && <span className="user-email">{userRole}</span>}
              </span>
              <span className="chevron">▾</span>
            </button>
            {showProfileMenu && (
              <div className="profile-dropdown" role="menu">
                <div className="profile-dropdown-info">
                  <p className="profile-dropdown-name">{userDisplayName}</p>
                  {userEmail && <p className="profile-dropdown-email">{userEmail}</p>}
                  {userRole && <p className="profile-dropdown-role">{userRole}</p>}
                </div>
                {hasFullAccess && (
                  <button type="button" onClick={() => handleProfileAction('my-profile')}>
                    My Profile
                  </button>
                )}
                <button
                  type="button"
                  className="secondary"
                  onClick={() => handleProfileAction('sign-out')}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="monitored-websites-section">
          <div className="monitored-websites-section-header">
            <h2>Monitored Websites</h2>
            {hasFullAccess && (
              <button className="add-new-website-button" onClick={() => setShowModal(true)}>+ Add New Website</button>
            )}
          </div>
          {error && (
            <div style={{ 
              padding: '12px', 
              marginBottom: '16px', 
              backgroundColor: '#fee', 
              color: '#c33', 
              borderRadius: '4px',
              border: '1px solid #fcc'
            }}>
              {error}
            </div>
          )}
          {loading ? (
            <p>Loading websites...</p>
          ) : sites.length === 0 ? (
            <p>No sites available.</p>
          ) : (
            <div className="table-container">
              <table className="monitored-websites-table" style={{ padding: 0 }}>
                <thead>
                  <tr>
                    <th>WEBSITE</th>
                    <th>URL</th>
                    <th>STATUS</th>
                    <th>LAST SCAN</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map(site => {
                    const status = site.status?.toLowerCase() || 'unknown';
                    const isCritical = status === 'critical';
                    const isHealthy = status === 'healthy';
                    const formatLastScan = (dateString) => {
                      if (!dateString) return 'N/A';
                      const date = new Date(dateString);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      let hours = date.getHours();
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      const ampm = hours >= 12 ? 'pm' : 'am';
                      hours = hours % 12;
                      hours = hours ? hours : 12;
                      const hoursStr = String(hours).padStart(2, '0');
                      return `${day}/${month}/${year}, ${hoursStr}:${minutes}:${seconds} ${ampm}`;
                    };
                    const lastScanDate = formatLastScan(site.lastChecked);
                    
                    return (
                      <tr key={site._id}>
                        <td>
                          <div className="website-cell">
                            <div className={`website-icon-container ${isCritical ? 'critical' : isHealthy ? 'healthy' : 'unknown'}`}>
                              {isCritical ? (
                                <span className="website-icon critical-icon">🚫</span>
                              ) : isHealthy ? (
                                <span className="website-icon healthy-icon">🌐</span>
                              ) : (
                                <span className="website-icon unknown-icon">🌐</span>
                              )}
                            </div>
                            <span className="website-name">{site.name}</span>
                          </div>
                        </td>
                        <td>
                          <a href={site.url} target="_blank" rel="noreferrer" className="website-url">
                            {site.url}
                          </a>
                        </td>
                        <td>
                          <span className={`status-badge-table ${status}`}>
                            <span className="status-indicator"></span>
                            {site.status === 'Unknown' || site.status === undefined 
                              ? 'Checking...' 
                              : site.status.charAt(0).toUpperCase() + site.status.slice(1).toLowerCase()}
                          </span>
                        </td>
                        <td className="last-scan-cell">{lastScanDate}</td>
                        <td>
                          <div className="table-actions">
                            <Link 
                              to={`/sites/${site._id}`} 
                              state={{ siteData: site }}
                              className="action-button view-button" 
                              title="View details"
                            >
                              <i className="fa-solid fa-eye"></i>
                            </Link>
                            {hasFullAccess && (
                              <button 
                                className="action-button delete-button"
                                onClick={() => handleRemoveSite(site._id, site.name)}
                                title="Delete website"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add New Website Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Add New Website</h3>
            <input
              type="text"
              placeholder="Site Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <input
              type="url"
              placeholder="Site URL"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            />
            <input
              type="email"
              placeholder="Notification Email"
              value={formData.ownerEmail}
              onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
            />
            <label className="platform-select">
              <span>Website Type</span>
              <select
                value={formData.platform}
                onChange={(e) => {
                  const platform = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    platform,
                  }));
                }}
              >
                <option value="wordpress">WordPress website</option>
              </select>
            </label>
            {formData.platform === 'wordpress' && (
              <>
                <input
                  type="text"
                  placeholder="WordPress Admin Username (for App Password)"
                  value={formData.wpAppUsername}
                  onChange={(e) => setFormData({ ...formData, wpAppUsername: e.target.value })}
                />
                <input
                  type="password"
                  placeholder="WordPress Application Password"
                  value={formData.wpAppPassword}
                  onChange={(e) => setFormData({ ...formData, wpAppPassword: e.target.value })}
                />
              </>
            )}
            <div className="modal-buttons">
              <button onClick={handleAddSite}>Add Website</button>
              <button onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteDashboard;